import React, { useEffect, useState, BaseSyntheticEvent } from 'react';
import _ from 'lodash';
//
import TextInput from 'components/input/text';
import InputTextarea from 'components/input/textarea';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  ContentSection,
  CostsSectionPanel,
  InputError,
  InputGroup,
  InputGroupHeader,
  Label,
  SaveFormulaButton,
  ScrollableContentSectionPanelCosts,
  ShowMoreButton,
  Title,
  TitleContainer,
  InputWrapper,
} from 'components/formula-entry/modals/_common';
import { EProductField, EProductNestedField, IFieldOption, IPrice } from 'store/field-options/types';
import uuid from 'uuid';
import labels from 'constants/labels';
import { EVolumeUnit } from 'data/volume-units';
import { TCostsModalProps } from 'components/formula-entry/modals/costs';
import { getMfgCostFormulaByName } from 'components/formula-entry/modals/costs/selectors';
import Select from 'components/input/select';
import MenuItem from 'components/menu-item';
import FormControl from 'components/input/form-control';
import FormulaInput from 'components/formula-entry/formula';
import { ECostFormulaType, ICostFormulaGroup } from 'store/cost-formulas/types';
import { ELoadingType } from 'store/loading/types';
import DotIconStyled from 'components/formula-entry/modals/costs/DotIconStyled';
import CostsOtherBatna from 'components/formula-entry/modals/costs/CostsOtherBatna';
import {
  initialState,
  errorsInitialState,
  getNewErrorsAndAreOtherBatnasErrorFree,
  IErrorsInitialStatesProps,
  IInitialStateProps,
} from './costs-utility';

const checkPriceCostReimbursedFreightFixedIncInventory = (newErrors: IErrorsInitialStatesProps) => {
  return (
    !newErrors.priceCostReimbursed &&
    !newErrors.priceCostFreight &&
    !newErrors.priceCostFixed &&
    !newErrors.priceCostIncInventory
  );
};

const checkPriceCostTransitDaysReimbursedFreightCostFixed = (newErrors: IErrorsInitialStatesProps) => {
  return (
    !newErrors.priceCostTransitDays &&
    !newErrors.batnaCostReimbursed &&
    !newErrors.batnaCostFreight &&
    !newErrors.batnaCostFixed
  );
};

const checkBatnaCostIncInventoryTransitDaysOtherBatnasError = (
  newErrors: IErrorsInitialStatesProps,
  areOtherBatnasErrorFree: boolean
) => {
  return !newErrors.batnaCostIncInventory && !newErrors.batnaCostTransitDays && areOtherBatnasErrorFree;
};

const checkExpandedSectionDescription = (expandedSection: { description: string }, description: string) => {
  return expandedSection.description === description;
};

const checkVolumeUnit = (volumeUnit: string) => {
  return volumeUnit === EVolumeUnit.kton || volumeUnit === EVolumeUnit.ton;
};

const getShowMoreButtonPrice = (costs: IInitialStateProps) => {
  return (
    Number.parseInt(costs.priceCostFixed, 10) ||
    Number.parseInt(costs.priceCostIncInventory, 10) ||
    Number.parseInt(costs.priceCostTransitDays, 10)
  );
};

const getShowMoreButtonBatna = (costs: IInitialStateProps) => {
  return (
    Number.parseInt(costs.batnaCostFixed, 10) ||
    Number.parseInt(costs.batnaCostIncInventory, 10) ||
    Number.parseInt(costs.batnaCostTransitDays, 10)
  );
};

const getProductIndex = (productIndex: number | null) => {
  return productIndex ?? 0;
};

// Label based on selected product volumeUnit (one-to-many mapping)
// Ton-related = $/t
// Pound-related = cpp
const getLabel = (volumeUnit: string) => {
  if (checkVolumeUnit(volumeUnit)) {
    return labels.currency.dollarsPerTon;
  }
  return labels.currency.centsPerPound;
};

const checkIfErrorExist = (newErrors: IErrorsInitialStatesProps, areOtherBatnasErrorFree: boolean) => {
  return (
    checkPriceCostReimbursedFreightFixedIncInventory(newErrors) &&
    checkPriceCostTransitDaysReimbursedFreightCostFixed(newErrors) &&
    checkBatnaCostIncInventoryTransitDaysOtherBatnasError(newErrors, areOtherBatnasErrorFree)
  );
};

const getMfgCostNameParsed = (costs: IInitialStateProps) => {
  return costs.mfgCostName || null;
};

const displayShowMoreButtonPrice = (showMoreButtonPrice: number) => {
  return showMoreButtonPrice ? <DotIconStyled /> : null;
};

const displayShowMoreButtonBatna = (showMoreButtonBatna: number) => {
  return showMoreButtonBatna ? <DotIconStyled /> : null;
};

export const shouldRenderSection = (
  expandedSections: { description: string; expanded: boolean }[],
  description: string
) => {
  let shouldRender = false;
  if (!expandedSections) {
    return shouldRender;
  }
  expandedSections.forEach((expandedSection: { description: string; expanded: boolean }) => {
    if (expandedSection.description === description && expandedSection.expanded) {
      shouldRender = true;
    }
  });
  return shouldRender;
};

const handleSectionClick = (
  description: string,
  expandedSections: { expanded: boolean; description: string }[],
  setExpandedSections: Function
) => {
  const newExpandedSections = expandedSections.map((expandedSection: { expanded: boolean; description: string }) => {
    if (checkExpandedSectionDescription(expandedSection, description)) {
      return {
        ...expandedSection,
        expanded: !expandedSection.expanded,
      };
    }
    return expandedSection;
  });
  setExpandedSections(newExpandedSections);
};

// Update formula when name changes
function handleDropdownChange(
  e: BaseSyntheticEvent,
  setCosts: Function,
  costs: IInitialStateProps,
  availableCostFormulaGroups: ICostFormulaGroup[]
) {
  setCosts({
    ...costs,
    mfgCostName: e.target.value,
    mfgCostFormulaTrue: getMfgCostFormulaByName(ECostFormulaType.TRUE_COST, availableCostFormulaGroups, e.target.value),
    mfgCostFormulaItp: getMfgCostFormulaByName(ECostFormulaType.ITP, availableCostFormulaGroups, e.target.value),
  });
}

function handleOtherBatnaOnChange(
  index: number,
  field: string,
  value: string,
  costs: IInitialStateProps,
  setCosts: Function
) {
  const otherBatnasUpdated: IPrice[] = _.cloneDeep(costs.otherBatnas);
  otherBatnasUpdated[index][field] = value;
  setCosts({
    ...costs,
    otherBatnas: otherBatnasUpdated,
  });
}

// Update costs in product
async function save(
  costs: IInitialStateProps,
  productIndex: number | null,
  updateProductField: Function,
  getDealEvaluation: Function,
  cancelFormulaEdit: Function,
  setErrors: Function,
  setLoading: Function
) {
  // Parse mfgCost
  const mfgCostNameParsed = getMfgCostNameParsed(costs);

  // Validate input values are positive numbers
  // Validate x number of otherBatnas (costFreight, costReimbursed)
  const {
    newErrors,
    areOtherBatnasErrorFree,
    priceCostFreightParsed,
    priceReimbursedParsed,
    priceCostFixedParsed,
    priceCostIncInventoryParsed,
    priceCostTransitDaysParsed,
    batnaCostFreightParsed,
    batnaReimbursedParsed,
    batnaCostIncInventoryParsed,
    batnaCostTransitDaysParsed,
    batnaCostFixedParsed,
    otherBatnasParsed,
  } = getNewErrorsAndAreOtherBatnasErrorFree(costs);

  // Update deal, re-evaluate, and close modal if no errors exist
  if (checkIfErrorExist(newErrors, areOtherBatnasErrorFree) && productIndex !== null) {
    updateProductField(
      productIndex,
      EProductField.BASE_PRICE,
      priceCostFreightParsed,
      EProductNestedField.COST_FREIGHT
    );
    updateProductField(
      productIndex,
      EProductField.BASE_PRICE,
      priceReimbursedParsed,
      EProductNestedField.COST_REIMBURSED
    );
    updateProductField(productIndex, EProductField.BASE_PRICE, priceCostFixedParsed, EProductNestedField.COST_FIXED);
    updateProductField(
      productIndex,
      EProductField.BASE_PRICE,
      priceCostIncInventoryParsed,
      EProductNestedField.COST_INVENTORY
    );
    updateProductField(
      productIndex,
      EProductField.BASE_PRICE,
      priceCostTransitDaysParsed,
      EProductNestedField.COST_TRANSIT_DAYS
    );
    updateProductField(
      productIndex,
      EProductField.MAIN_BATNA,
      batnaCostFreightParsed,
      EProductNestedField.COST_FREIGHT
    );
    updateProductField(
      productIndex,
      EProductField.MAIN_BATNA,
      batnaReimbursedParsed,
      EProductNestedField.COST_REIMBURSED
    );
    updateProductField(
      productIndex,
      EProductField.MAIN_BATNA,
      batnaCostIncInventoryParsed,
      EProductNestedField.COST_INVENTORY
    );
    updateProductField(
      productIndex,
      EProductField.MAIN_BATNA,
      batnaCostTransitDaysParsed,
      EProductNestedField.COST_TRANSIT_DAYS
    );
    updateProductField(productIndex, EProductField.MAIN_BATNA, batnaCostFixedParsed, EProductNestedField.COST_FIXED);
    updateProductField(productIndex, EProductField.OTHER_BATNAS, otherBatnasParsed);
    updateProductField(productIndex, EProductField.MANUFACTURING_COST, mfgCostNameParsed);
    getDealEvaluation();
    cancelFormulaEdit();
  } else {
    setErrors(newErrors);
    setLoading(ELoadingType.NONE);
  }
}

const RenderBatnaExpandedSection = ({
  errors,
  costs,
  setCosts,
  volumeUnit,
  expandedSections,
  setExpandedSections,
  batnaDescription,
}: {
  errors: IErrorsInitialStatesProps;
  costs: IInitialStateProps;
  setCosts: Function;
  volumeUnit: string;
  expandedSections: { expanded: boolean; description: string }[];
  setExpandedSections: Function;
  batnaDescription: string;
}) => {
  return (
    <div className="hidden-area">
      <InputGroup>
        <Label data-valid={!!errors.batnaCostFixed} htmlFor="batna-cost-fixed">
          FIXED ANNUAL SUPPLY COST ($/YR)
        </Label>
        <TextInput
          id="batna-cost-fixed"
          value={costs.batnaCostFixed}
          onChange={e => setCosts({ ...costs, batnaCostFixed: e.target.value })}
        />
        <InputError>{errors.batnaCostFixed}</InputError>
      </InputGroup>

      <InputGroup>
        <Label data-valid={!!errors.batnaCostIncInventory} htmlFor="batna-cost-inc-inventory">
          ADDITIONAL INVENTORY ({volumeUnit.toUpperCase()})
        </Label>
        <TextInput
          id="batna-cost-inc-inventory"
          value={costs.batnaCostIncInventory}
          onChange={e => setCosts({ ...costs, batnaCostIncInventory: e.target.value })}
        />
        <InputError>{errors.batnaCostIncInventory}</InputError>
      </InputGroup>

      <InputGroup>
        <Label data-valid={!!errors.batnaCostTransitDays} htmlFor="batna-cost-transit-days">
          ADDITIONAL TRANSIT DAYS
        </Label>
        <TextInput
          id="batna-cost-transit-days"
          value={costs.batnaCostTransitDays}
          onChange={e => setCosts({ ...costs, batnaCostTransitDays: e.target.value })}
        />
        <InputError>{errors.batnaCostTransitDays}</InputError>
      </InputGroup>
      <ShowMoreButton onClick={() => handleSectionClick(batnaDescription, expandedSections, setExpandedSections)}>
        show less
      </ShowMoreButton>
    </div>
  );
};

const RenderCostsExpandedSection = ({
  errors,
  costs,
  setCosts,
  volumeUnit,
  incoTerm,
  updateProductField,
  productIndex,
  defaultIncoTerms,
  transportMode,
  defaultTransportModes,
  otherSupply,
  priceDescription,
  expandedSections,
  setExpandedSections,
}: {
  errors: IErrorsInitialStatesProps;
  costs: IInitialStateProps;
  setCosts: Function;
  volumeUnit: string;
  incoTerm: string;
  updateProductField: Function;
  productIndex: number | null;
  defaultIncoTerms?: IFieldOption[];
  transportMode: string;
  defaultTransportModes?: IFieldOption[];
  otherSupply: string;
  priceDescription: string;
  expandedSections: { expanded: boolean; description: string }[];
  setExpandedSections: Function;
}) => {
  return (
    <div className="hidden-area">
      <InputGroup>
        <Label data-valid={!!errors.priceCostFixed} htmlFor="price-cost-fixed">
          FIXED ANNUAL SUPPLY COST ($/YR)
        </Label>
        <TextInput
          id="price-cost-fixed"
          value={costs.priceCostFixed}
          onChange={e => setCosts({ ...costs, priceCostFixed: e.target.value })}
        />
        <InputError>{errors.priceCostFixed}</InputError>
      </InputGroup>

      <InputGroup>
        <Label data-valid={!!errors.priceCostIncInventory} htmlFor="price-cost-inc-inventory">
          ADDITIONAL INVENTORY ({volumeUnit.toUpperCase()})
        </Label>
        <TextInput
          id="price-cost-inc-inventory"
          value={costs.priceCostIncInventory}
          onChange={e => setCosts({ ...costs, priceCostIncInventory: e.target.value })}
        />
        <InputError>{errors.priceCostIncInventory}</InputError>
      </InputGroup>

      <InputGroup>
        <Label data-valid={!!errors.priceCostTransitDays} htmlFor="price-cost-transit-days">
          ADDITIONAL TRANSIT DAYS
        </Label>
        <TextInput
          id="price-cost-transit-days"
          value={costs.priceCostTransitDays}
          onChange={e => setCosts({ ...costs, priceCostTransitDays: e.target.value })}
        />
        <InputError>{errors.priceCostTransitDays}</InputError>
      </InputGroup>
      <InputWrapper>
        <Label>INCOTERM</Label>
        <FormControl variant="outlined" required>
          <Select
            labelId="select-label-incoterm"
            id="select-incoterm"
            value={incoTerm}
            onChange={e => updateProductField(getProductIndex(productIndex), EProductField.INCOTERM, e.target.value)}
          >
            <MenuItem value="">Select incoterm</MenuItem>
            {defaultIncoTerms?.map((incoTermData: IFieldOption) => (
              <MenuItem key={incoTermData.name} value={incoTermData.name}>
                {incoTermData.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </InputWrapper>
      <InputWrapper>
        <Label>TRANSPORT MODE</Label>
        <FormControl variant="outlined" required>
          <Select
            labelId="select-label-transport"
            id="select-transport"
            value={transportMode}
            onChange={e =>
              updateProductField(getProductIndex(productIndex), EProductField.TRANSPORT_MODE, e.target.value)
            }
          >
            <MenuItem value="">Select transport mode</MenuItem>
            {defaultTransportModes?.map((transportModeData: IFieldOption) => (
              <MenuItem key={transportModeData.name} value={transportModeData.name}>
                {transportModeData.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="other-supply-item">OTHER SUPPLY ITEMS</Label>
        <InputTextarea
          id="other-supply-item"
          value={otherSupply}
          onChange={e => updateProductField(getProductIndex(productIndex), EProductField.OTHER_SUPPLY, e.target.value)}
        />
      </InputWrapper>
      <ShowMoreButton onClick={() => handleSectionClick(priceDescription, expandedSections, setExpandedSections)}>
        show less
      </ShowMoreButton>
    </div>
  );
};

export default ({
  cancelFormulaEdit,
  getDealEvaluation,
  setLoading,
  updateProductField,
  priceDescription,
  priceCostFreight,
  priceCostReimbursed,
  priceCostFixed,
  priceCostIncInventory,
  priceCostTransitDays,
  batnaDescription,
  batnaCostFreight,
  batnaCostReimbursed,
  batnaCostFixed,
  batnaCostIncInventory,
  batnaCostTransitDays,
  productIndex,
  volumeUnit,
  availableCostFormulaGroups,
  mfgCostName,
  mfgCostFormulaTrue,
  mfgCostFormulaItp,
  otherBatnas,
  defaultIncoTerms,
  defaultTransportModes,
  incoTerm,
  transportMode,
  otherSupply,
}: TCostsModalProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [errors, setErrors] = useState(errorsInitialState);
  const [costs, setCosts] = useState(initialState);
  const [expandedSections, setExpandedSections] = useState<{ expanded: boolean; description: string }[]>([]);

  useEffect(() => {
    if (!isInitialized) {
      // initialize error object (with otherBatnas)
      const newErrors: IErrorsInitialStatesProps = errorsInitialState;

      // initialize expanded sections
      const newExpandedSections = [
        {
          description: priceDescription,
          expanded: false,
        },
        {
          description: batnaDescription,
          expanded: false,
        },
      ];

      if (otherBatnas.length) {
        otherBatnas.forEach(otherBatna => {
          newErrors.otherBatnas.push({ costFreight: '', costReimbursed: '' });
          newExpandedSections.push({
            description: otherBatna.description,
            expanded: false,
          });
        });
      }

      setExpandedSections(newExpandedSections);

      setCosts({
        priceCostFreight,
        priceCostReimbursed,
        priceCostFixed,
        priceCostIncInventory,
        priceCostTransitDays,
        batnaCostFreight,
        batnaCostReimbursed,
        batnaCostFixed,
        batnaCostIncInventory,
        batnaCostTransitDays,
        mfgCostName,
        mfgCostFormulaTrue,
        mfgCostFormulaItp,
        otherBatnas,
      });

      setErrors(newErrors);
      setIsInitialized(true);
    }
  }, [
    isInitialized,
    priceDescription,
    priceCostFreight,
    priceCostReimbursed,
    priceCostFixed,
    priceCostIncInventory,
    priceCostTransitDays,
    batnaDescription,
    batnaCostFreight,
    batnaCostReimbursed,
    batnaCostFixed,
    batnaCostIncInventory,
    batnaCostTransitDays,
    mfgCostName,
    mfgCostFormulaTrue,
    mfgCostFormulaItp,
    otherBatnas,
  ]);

  const pricePerUnitLabel = getLabel(volumeUnit);
  const showMoreButtonPrice = getShowMoreButtonPrice(costs);
  const showMoreButtonBatna = getShowMoreButtonBatna(costs);

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Costs</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <ContentSection>
            <ScrollableContentSectionPanelCosts>
              <InputGroupHeader>{`BASE - ${priceDescription}`}</InputGroupHeader>
              <InputGroup>
                <Label data-valid={!!errors.priceCostReimbursed} htmlFor="price-reimbursed">
                  REIMBURSED SUPPLY COST ({pricePerUnitLabel})
                </Label>
                <TextInput
                  id="price-reimbursed"
                  value={costs.priceCostReimbursed}
                  onChange={e => setCosts({ ...costs, priceCostReimbursed: e.target.value })}
                />
                <InputError>{errors.priceCostReimbursed}</InputError>
              </InputGroup>

              <InputGroup>
                <Label data-valid={!!errors.priceCostFreight} htmlFor="price-cost-freight">
                  VARIABLE SUPPLY COST ({pricePerUnitLabel})
                </Label>
                <TextInput
                  id="price-cost-freight"
                  value={costs.priceCostFreight}
                  onChange={e => setCosts({ ...costs, priceCostFreight: e.target.value })}
                />
                <InputError>{errors.priceCostFreight}</InputError>
              </InputGroup>

              {shouldRenderSection(expandedSections, priceDescription) ? (
                <RenderCostsExpandedSection
                  errors={errors}
                  costs={costs}
                  volumeUnit={volumeUnit}
                  incoTerm={incoTerm}
                  productIndex={productIndex}
                  defaultIncoTerms={defaultIncoTerms}
                  transportMode={transportMode}
                  defaultTransportModes={defaultTransportModes}
                  otherSupply={otherSupply}
                  priceDescription={priceDescription}
                  expandedSections={expandedSections}
                  updateProductField={updateProductField}
                  setCosts={setCosts}
                  setExpandedSections={setExpandedSections}
                />
              ) : (
                <ShowMoreButton
                  onClick={() => handleSectionClick(priceDescription, expandedSections, setExpandedSections)}
                >
                  show more {displayShowMoreButtonPrice(showMoreButtonPrice)}
                </ShowMoreButton>
              )}

              <br />
              <InputGroupHeader>{`BATNA - ${batnaDescription}`}</InputGroupHeader>
              <InputGroup>
                <Label data-valid={!!errors.batnaCostReimbursed} htmlFor="batna-cost-reimbursed">
                  REIMBURSED SUPPLY COST ({pricePerUnitLabel})
                </Label>
                <TextInput
                  id="batna-cost-reimbursed"
                  value={costs.batnaCostReimbursed}
                  onChange={e => setCosts({ ...costs, batnaCostReimbursed: e.target.value })}
                />
                <InputError>{errors.batnaCostReimbursed}</InputError>
              </InputGroup>

              <InputGroup>
                <Label data-valid={!!errors.batnaCostFreight} htmlFor="batna-cost-freight">
                  VARIABLE SUPPLY COST ({pricePerUnitLabel})
                </Label>
                <TextInput
                  id="batna-cost-freight"
                  value={costs.batnaCostFreight}
                  onChange={e => setCosts({ ...costs, batnaCostFreight: e.target.value })}
                />
                <InputError>{errors.batnaCostFreight}</InputError>
              </InputGroup>

              {shouldRenderSection(expandedSections, batnaDescription) ? (
                <RenderBatnaExpandedSection
                  errors={errors}
                  costs={costs}
                  setCosts={setCosts}
                  volumeUnit={volumeUnit}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                  batnaDescription={batnaDescription}
                />
              ) : (
                <ShowMoreButton
                  onClick={() => handleSectionClick(batnaDescription, expandedSections, setExpandedSections)}
                >
                  show more {displayShowMoreButtonBatna(showMoreButtonBatna)}
                </ShowMoreButton>
              )}

              {/* otherBatnas */}
              {costs.otherBatnas.map((otherBatna: IPrice, index: number) => (
                <CostsOtherBatna
                  errors={errors}
                  expandedSections={expandedSections}
                  handleOtherBatnaOnChange={handleOtherBatnaOnChange}
                  handleSectionClick={handleSectionClick}
                  index={index}
                  key={`cost-other-batna-${uuid()}`}
                  otherBatna={otherBatna}
                  pricePerUnitLabel={pricePerUnitLabel}
                  volumeUnit={volumeUnit}
                />
              ))}
            </ScrollableContentSectionPanelCosts>

            <CostsSectionPanel>
              <InputGroupHeader />
              <InputGroup>
                <Label htmlFor="select-manufacturing-cost">VARIABLE MANUFACTURING COST LABEL</Label>
                <FormControl variant="outlined" required>
                  <Select
                    id="select-manufacturing-cost"
                    value={costs.mfgCostName}
                    onChange={e => handleDropdownChange(e, setCosts, costs, availableCostFormulaGroups)}
                  >
                    <MenuItem value="">Select cost formula</MenuItem>
                    {availableCostFormulaGroups?.map((availableCostFormulaGroup: ICostFormulaGroup) => (
                      <MenuItem
                        key={availableCostFormulaGroup.name}
                        value={availableCostFormulaGroup.name}
                        data-testid="cost-formula-select-option"
                      >
                        {availableCostFormulaGroup.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <InputError />
              </InputGroup>

              {costs.mfgCostFormulaTrue && (
                <InputGroup>
                  <Label>INTEGRATED COST FORMULA ({pricePerUnitLabel})</Label>
                  <FormulaInput formulaInputValue={costs.mfgCostFormulaTrue} readOnly />
                  <InputError />
                </InputGroup>
              )}

              {costs.mfgCostFormulaItp && (
                <InputGroup>
                  <Label>ITP-BASED COST FORMULA ({pricePerUnitLabel})</Label>
                  <FormulaInput formulaInputValue={costs.mfgCostFormulaItp} readOnly />
                  <InputError />
                </InputGroup>
              )}
            </CostsSectionPanel>
          </ContentSection>

          <ButtonContainer>
            <CancelButton onClick={cancelFormulaEdit}>Cancel</CancelButton>
            <SaveFormulaButton
              onClick={() =>
                save(
                  costs,
                  productIndex,
                  updateProductField,
                  getDealEvaluation,
                  cancelFormulaEdit,
                  setErrors,
                  setLoading
                )
              }
            >
              Apply Costs
            </SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
